import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const readEdits = (storageKey) => {
  if (!storageKey) return {};
  try {
    const raw = localStorage.getItem(storageKey);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const useFunnelRowEdits = (storageKey) => {
  const [edits, setEdits] = useState(() => readEdits(storageKey));

  const persist = useCallback(
    (next) => {
      if (!storageKey) return;
      try {
        if (Object.keys(next).length > 0) {
          localStorage.setItem(storageKey, JSON.stringify(next));
        } else {
          localStorage.removeItem(storageKey);
        }
      } catch {
        return;
      }
    },
    [storageKey]
  );

  useEffect(() => {
    setEdits(readEdits(storageKey));
  }, [storageKey]);

  const setRowEdit = useCallback(
    (id, fields) => {
      setEdits((prev) => {
        const next = { ...prev, [id]: fields };
        persist(next);
        return next;
      });
    },
    [persist]
  );

  const resetRow = useCallback(
    (id) => {
      setEdits((prev) => {
        if (!(id in prev)) return prev;
        const next = { ...prev };
        delete next[id];
        persist(next);
        return next;
      });
    },
    [persist]
  );

  const clearAll = useCallback(() => {
    setEdits({});
    persist({});
  }, [persist]);

  const clearAllRef = useRef(clearAll);
  clearAllRef.current = clearAll;

  useEffect(() => () => clearAllRef.current(), []);

  const editedIds = useMemo(() => new Set(Object.keys(edits)), [edits]);

  return {
    edits,
    editedIds,
    isDirty: editedIds.size > 0,
    setRowEdit,
    resetRow,
    clearAll,
  };
};

export default useFunnelRowEdits;
